import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GitaAudioIcon } from "@/components/audio/GitaAudioIcon";
import { TriLanguageBlock } from "@/components/i18n/TriLanguageBlock";
import { guruToTriItem } from "@/lib/i18n/tri-language";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Phase 8 — Gītā verse: tri-language (Sanskrit + English + preferred).
 */
export function GitaVerseCard({ verse, gurusById = {}, token }) {
  const { t } = useI18n();
  const slokaText = verse.sanskrit_devanagari || verse.sanskrit_iast || "";

  return (
    <Card className="bg-white/80" data-testid={`gita-verse-${verse.verse}`}>
      <CardContent className="p-4 space-y-3 text-sm" data-testid="gita-shastra-verse">
        <div className="flex justify-between items-start gap-2">
          <span className="text-xs font-medium text-primary">Śloka {verse.verse}</span>
          <GitaAudioIcon
            audioRef={verse.audio_ref}
            verseText={slokaText}
            iastText={verse.sanskrit_iast}
            token={token}
            title={t("btn.listen") || "Listen to śloka"}
          />
        </div>

        <TriLanguageBlock item={verse} meaningPrefix="translation" meaningAudioMode="gita" token={token} />

        {verse.meaning_for_sadhaka && (
          <TriLanguageBlock
            item={{ meaning_en: verse.meaning_for_sadhaka, ...verse }}
            meaningPrefix="meaning"
            meaningAudioMode="sadhana"
            englishLabel="Practical insight"
            token={token}
            compact
          />
        )}

        {verse.story_context && (
          <TriLanguageBlock
            item={{ meaning_en: verse.story_context, ...verse }}
            meaningPrefix="meaning"
            meaningAudioMode="commentary"
            englishLabel="Context"
            token={token}
            compact
            className="text-xs"
          />
        )}

        {verse.tattva_links?.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="gita-tattva">
            {verse.tattva_links.map((tattva) => (
              <Badge key={tattva} variant="outline" className="text-xs capitalize">
                {tattva}
              </Badge>
            ))}
          </div>
        )}

        {verse.guru_refs?.length > 0 && (
          <div data-testid="gita-guru-refs">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Guru insights</p>
            <ul className="space-y-2">
              {verse.guru_refs.map((id) => {
                const guru = gurusById[id];
                return (
                  <li key={id} className="text-xs border-l-2 border-primary/20 pl-2">
                    <TriLanguageBlock
                      item={guruToTriItem(guru || { id, translation_en: id })}
                      meaningPrefix="translation"
                      compact
                      token={token}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
